import React, { useState, useRef, useEffect, useCallback } from 'react';
import { streamAtlasAI } from '@/api/portal';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
}

export interface AtlasAIChatProps {
  isOpen: boolean;
  onClose: () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useAtlasAIChat() {
  const [isOpen, setIsOpen] = useState(false);
  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  };
}

// ── Icons ─────────────────────────────────────────────────────────────────────
//
// Arul 2026-05-11 — TEME (Test Equipment Made Easy) mascot replaces the
// old generic robot. The real cocker-spaniel illustration lives at
// `apps/frontend/public/teme-mascot.jpg`; the inline SVG below is the
// fallback for environments that haven't synced the asset yet.
function TemeIcon({ className }: { className?: string }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (imgFailed) return <TemeDogSvg className={className} />;
  return (
    <img
      src="/teme-mascot.jpg"
      alt="TEME"
      className={className}
      onError={() => setImgFailed(true)}
    />
  );
}

/** Inline SVG fallback — friendly dog face with the JM Test palette. */
function TemeDogSvg({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Floppy ears (left + right) */}
      <ellipse cx="7" cy="11" rx="4" ry="6" fill="#b88456" />
      <ellipse cx="25" cy="11" rx="4" ry="6" fill="#b88456" />
      {/* Head */}
      <circle cx="16" cy="16" r="9" fill="#d8a878" />
      {/* Snout */}
      <ellipse cx="16" cy="20" rx="4.5" ry="3.5" fill="#f0d4ad" />
      {/* Nose */}
      <ellipse cx="16" cy="18.5" rx="1.4" ry="1" fill="#2a2a2a" />
      {/* Eyes */}
      <circle cx="12.5" cy="14.5" r="1.3" fill="#2a2a2a" />
      <circle cx="19.5" cy="14.5" r="1.3" fill="#2a2a2a" />
      {/* Eye highlights */}
      <circle cx="12.9" cy="14.1" r="0.45" fill="#fff" />
      <circle cx="19.9" cy="14.1" r="0.45" fill="#fff" />
      {/* Smile */}
      <path d="M13.5 21.5 Q16 23.2 18.5 21.5" stroke="#2a2a2a" strokeWidth="1.2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// ── Inline markdown renderer (no new deps) ────────────────────────────────────
//
// Tight, opinionated subset of markdown that matches what the new Atlas
// system prompt is allowed to produce: paragraphs, bullet/numbered lists,
// inline links, inline code, fenced code blocks, and bold (only when the
// model breaks the "plain punctuation only" rule, which it shouldn't —
// but we render bold cleanly if it slips through). No tables / no
// headings (the prompt allows headings only on long answers; the chat
// surface is short-answer-first; if we need them later we add them).
//
// The renderer is deliberately small — Atlas needs to feel snappy and a
// dependency on react-markdown + remark + rehype would bloat the bundle.
// Trade-off: we don't handle every markdown edge case. We DO handle the
// shapes our prompt actually emits.

const INLINE_RE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]+\))/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let key = 0;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Reset state per call.
  INLINE_RE.lastIndex = 0;
  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      parts.push(
        <strong key={`${keyPrefix}-${key++}`} className="font-semibold text-jm-text">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('`') && token.endsWith('`')) {
      parts.push(
        <code key={`${keyPrefix}-${key++}`} className="px-1 py-0.5 rounded bg-gray-100 border border-gray-200 text-[12.5px] font-mono text-jm-text">
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      // Link: [text](url)
      const closeBracket = token.indexOf(']');
      const openParen = token.indexOf('(', closeBracket);
      const label = token.slice(1, closeBracket);
      const href = token.slice(openParen + 1, -1);
      parts.push(
        <a
          key={`${keyPrefix}-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-jm-link hover:underline"
        >
          {label}
        </a>,
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
}

function renderMessage(text: string): React.ReactNode {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();

    // Fenced code block (```lang ... ```)
    if (/^```/.test(trimmed)) {
      const lang = trimmed.slice(3).trim();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test((lines[i] ?? '').trim())) {
        code.push(lines[i] ?? '');
        i++;
      }
      i++; // consume closing fence
      nodes.push(
        <pre
          key={key++}
          className="my-2 px-3 py-2 rounded-lg bg-gray-900 text-gray-100 text-[12.5px] font-mono overflow-x-auto"
        >
          {lang && <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{lang}</div>}
          <code>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Blank line → paragraph break
    if (!trimmed) {
      nodes.push(<div key={key++} className="h-2" />);
      i++;
      continue;
    }

    // Bullet list
    if (/^[-•]\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-•]\s/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^[-•]\s/, ''));
        i++;
      }
      nodes.push(
        <ul key={key++} className="space-y-1 my-1">
          {items.map((item, idx) => (
            <li key={idx} className="flex gap-2 items-start">
              <span className="text-jm-navy mt-[7px] flex-shrink-0 leading-none text-[10px]">●</span>
              <span className="flex-1">{renderInline(item, `bl-${key}-${idx}`)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      nodes.push(
        <ol key={key++} className="space-y-1 my-1">
          {items.map((item, idx) => (
            <li key={idx} className="flex gap-2 items-start">
              <span className="text-jm-navy font-medium flex-shrink-0 min-w-[18px]">{idx + 1}.</span>
              <span className="flex-1">{renderInline(item, `ol-${key}-${idx}`)}</span>
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    // Regular paragraph line
    nodes.push(
      <p key={key++} className="leading-relaxed">
        {renderInline(line, `p-${key}`)}
      </p>,
    );
    i++;
  }

  return <div className="text-[14px] leading-relaxed">{nodes}</div>;
}

// ── Component ─────────────────────────────────────────────────────────────────
export function AtlasAIChat({ isOpen, onClose }: AtlasAIChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content:
        "Hi, I'm TEME — Test Equipment Made Easy. Ask me anything about JMTS: policies, benefits, KPIs, who owns what, calibration records, the org chart, or whatever else you'd ask the smartest colleague in the company. I can also help with general questions outside JMTS.",
    },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  /** True after first delta arrives — flips the typing indicator off. */
  const [hasFirstToken, setHasFirstToken] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  // Cancel any in-flight stream on close so we don't leak fetch connections.
  useEffect(() => {
    if (!isOpen && abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, [isOpen]);

  // ESC closes the panel
  useEffect(() => {
    if (!isOpen) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;

    const userMessage: Message = { role: 'user', content: text };
    const history = messages.map(({ role, content }) => ({ role, content }));

    // Push the user turn + an empty assistant placeholder we stream into.
    setMessages((prev) => [...prev, userMessage, { role: 'assistant', content: '' }]);
    setInput('');
    setIsStreaming(true);
    setHasFirstToken(false);

    abortRef.current?.abort();
    abortRef.current = streamAtlasAI(text, history, {
      onSources: (sources) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = { ...last, sources };
          }
          return next;
        });
      },
      onDelta: (chunk) => {
        if (!hasFirstToken) setHasFirstToken(true);
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = { ...last, content: last.content + chunk };
          }
          return next;
        });
      },
      onDone: () => {
        setIsStreaming(false);
        abortRef.current = null;
      },
      onError: (err) => {
        setIsStreaming(false);
        abortRef.current = null;
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              content: last.content || `Sorry, I hit an error: ${err.message}`,
            };
          }
          return next;
        });
      },
    });
  }, [input, isStreaming, messages, hasFirstToken]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={onClose} aria-hidden="true" />
      )}

      {/* Chat panel — fixed bottom-right, slides up.
          Height caps:
            - Mobile: 90dvh (dynamic viewport height). Arul 2026-05-15
              fix — `vh` doesn't shrink when the on-screen keyboard
              opens on iOS/Android, so the keyboard occluded the Send
              button. `dvh` recalculates with the keyboard visible so
              the panel resizes and the input stays in view.
            - Desktop: target 640px BUT never exceed
              `calc(100vh - 7rem)` so the panel's top edge — and
              therefore the close X — is always inside the viewport.
              Without this cap, on shorter windows (laptops with
              browser chrome) the header gets pushed off the top of
              the screen and users can't reach the X. */}
      <div
        className={[
          'fixed z-50 flex flex-col',
          'bottom-0 right-0 w-full md:w-[460px]',
          'h-[90dvh] md:bottom-20 md:right-6',
          'md:h-[min(640px,calc(100vh-7rem))]',
          'bg-white rounded-t-2xl md:rounded-2xl shadow-2xl border border-gray-200',
          'transition-all duration-300 ease-in-out',
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none',
        ].join(' ')}
        aria-label="TEME Chat"
        role="dialog"
        aria-modal="true"
      >
        {/* Header — flex-shrink-0 so it never collapses out of view. */}
        <div className="flex items-center justify-between px-4 py-3 bg-jm-navy rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-jm-yellow flex items-center justify-center flex-shrink-0 overflow-hidden">
              <TemeIcon className="w-7 h-7" />
            </div>
            <div className="min-w-0">
              <p className="font-montserrat font-bold text-white text-sm">TEME</p>
              <p className="text-white/55 text-[11px] font-work-sans truncate">Test Equipment Made Easy · Tee-Mee</p>
            </div>
          </div>
          {/* Close button — high-contrast, large hit target, never
              dimmed against the navy background. Arul 2026-05-13:
              previously this was `bg-white/10` which was nearly
              invisible. */}
          <button
            onClick={onClose}
            className="ml-2 flex-shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-lg text-jm-navy bg-jm-yellow hover:bg-yellow-300 ring-1 ring-white/30 hover:ring-white/60 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Close TEME"
            title="Close (Esc)"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0 bg-gray-50/40">
          {messages.map((msg, i) => {
            const isLast = i === messages.length - 1;
            const showTyping = isLast && msg.role === 'assistant' && isStreaming && !hasFirstToken;
            return (
              <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-jm-yellow flex items-center justify-center flex-shrink-0 mt-1 overflow-hidden ring-2 ring-jm-navy/10">
                    <TemeIcon className="w-6 h-6" />
                  </div>
                )}
                <div
                  className={[
                    'max-w-[88%] px-4 py-3 rounded-2xl font-work-sans',
                    msg.role === 'user'
                      ? 'bg-jm-navy text-white rounded-tr-sm text-[14px] leading-relaxed'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm',
                  ].join(' ')}
                >
                  {msg.role === 'assistant' ? (
                    showTyping ? (
                      <span className="inline-flex items-center gap-1.5 py-1">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    ) : (
                      <>
                        {renderMessage(msg.content || ' ')}
                        {msg.sources && msg.sources.length > 0 && (
                          <div className="mt-3 pt-2.5 border-t border-gray-100">
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-work-sans mb-1.5">
                              Sources
                            </p>
                            <ul className="space-y-0.5">
                              {msg.sources.map((s, sIdx) => (
                                <li key={sIdx} className="text-[11.5px] text-gray-500 font-work-sans leading-snug">
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

        {/* Input — Arul 2026-05-15 mobile fix. `pb-[env(safe-area-inset-bottom)]`
            keeps the Send button clear of the iPhone home indicator strip
            below the home button on devices that have one. */}
        <div className="flex-shrink-0 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-gray-100 bg-white rounded-b-2xl">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask TEME anything…"
              disabled={isStreaming}
              className="flex-1 px-3.5 py-2.5 text-sm font-work-sans bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-jm-navy focus:border-transparent disabled:opacity-50 placeholder-gray-400"
            />
            <button
              onClick={() => void handleSend()}
              disabled={!input.trim() || isStreaming}
              className="p-2.5 rounded-xl bg-jm-navy text-white hover:bg-jm-navy-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              aria-label="Send"
            >
              <SendIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default AtlasAIChat;
